// biome-ignore-all lint/suspicious/noArrayIndexKey: TODO - address later
import { Trophy } from "lucide-react";
import { Link, useNavigate } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useRecentFirstTimeWonOpportunities } from "@/hooks/api/use-recent-first-time-won-opportunities";
import { formatDate } from "@/lib/date-formatting";
import { cn } from "@/lib/utils";

interface RecentWonOpportunitiesProps {
  days?: number;
  limit?: number;
  className?: string;
}

export function RecentWonOpportunities({
  days = 90,
  limit = 5,
  className,
}: RecentWonOpportunitiesProps) {
  const navigate = useNavigate();
  const {
    data: opportunities,
    isLoading,
    error,
  } = useRecentFirstTimeWonOpportunities(days, limit);

  const handleRowClick = (
    event: React.MouseEvent,
    accountId: string,
    opportunityId: string,
  ) => {
    const url = `/accounts/explorer?accountId=${accountId}&opportunityId=${opportunityId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };

  // Function to format currency
  const formatCurrency = (amount: number) => {
    return amount.toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    });
  };

  return (
    <Card
      className={cn("w-full", className)}
      data-testid="recent-won-opportunities-card"
    >
      <CardHeader className="flex flex-row items-center gap-x-3 pb-2">
        <Trophy className="h-9 w-9 text-supporting-yellow flex-shrink-0" />
        <div className="space-y-1">
          <CardTitle className="text-md">
            <span>Recent new logo wins</span>
          </CardTitle>
          <CardDescription>
            Selection of new business opportunities that recently closed
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="pt-4">
        {isLoading ? (
          <div className="space-y-3">
            {Array(3)
              .fill(0)
              .map((_, index) => (
                <div key={index} className="flex justify-between">
                  <Skeleton className="h-5 w-[200px]" />
                  <Skeleton className="h-5 w-[100px]" />
                </div>
              ))}
          </div>
        ) : error ? (
          <div className="text-sm text-muted-foreground py-2">
            Error loading recently won opportunities.
          </div>
        ) : !opportunities?.length ? (
          <div className="text-sm text-muted-foreground py-2">
            No recently won opportunities found.
          </div>
        ) : (
          <div className="space-y-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/2">Account / Opportunity</TableHead>
                  <TableHead className="text-right whitespace-nowrap w-1/4">
                    Amount
                  </TableHead>
                  <TableHead className="text-right whitespace-nowrap w-1/4">
                    Close Date
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {opportunities.map((opp) => (
                  <Tooltip key={opp.opportunity_id}>
                    <TooltipTrigger asChild>
                      <TableRow
                        onClick={(e) =>
                          handleRowClick(e, opp.account_id, opp.opportunity_id)
                        }
                        className="cursor-pointer hover:bg-accent group"
                        data-testid={`won-opportunity-row-${opp.opportunity_id}`}
                      >
                        <TableCell>
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-medium">
                                {opp.account_name}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {opp.opportunity_name}
                              </div>
                            </div>
                            <Link
                              to={`/accounts/explorer?accountId=${opp.account_id}&opportunityId=${opp.opportunity_id}`}
                              onClick={(e) => e.stopPropagation()}
                            ></Link>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          {opp.total_amount
                            ? formatCurrency(opp.total_amount)
                            : "-"}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatDate(opp.close_date)}
                        </TableCell>
                      </TableRow>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View deal cycle in account story</p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
